import React, { FC, useEffect, useState } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import { NavCloseIcon } from 'assets/icons';
import WalletBankAccountScreen from 'containers/legal-reacceptance/screens/WalletBankAccountScreen';
import LegalReacceptanceSuccessScreen from 'containers/legal-reacceptance/screens/LegalReacceptanceSuccessScreen';
import { useSubmitWalletBankAccountMutation } from 'containers/legal-reacceptance/api';
import {
  CleverTapEventsLegalReacceptance,
  WalletBankAccountPayload,
  WalletBankViewSource
} from 'containers/legal-reacceptance/legalReacceptance.model';
import { useAppDispatch } from 'hooks';
import { showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { logAnalyticEvent } from 'utils/analytics';

export interface WalletBankAccountReconnectModalProps {
  open: boolean;
  onClose: () => void;
  teamId: number;
  onAfterComplete: () => void;
  viewSource: WalletBankViewSource;
  walletBankScreenSeenCount: number;
}

type WalletBankReconnectStep = 'bank' | 'success';

const FALLBACK_WALLET_BANK_SUBMIT_ERROR_MESSAGE =
  'Unable to submit bank account details right now. Please verify the details and try again.';

const getWalletBankSubmitErrorDetails = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const errorData = (error as { data?: { details?: unknown } })?.data;
  const details = errorData?.details;
  if (typeof details !== 'string') {
    return null;
  }
  const trimmedDetails = details.trim();
  return trimmedDetails.length > 0 ? trimmedDetails : null;
};

const WalletBankAccountReconnectModal: FC<WalletBankAccountReconnectModalProps> = ({
  open,
  onClose,
  teamId,
  onAfterComplete,
  viewSource,
  walletBankScreenSeenCount
}) => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<WalletBankReconnectStep>('bank');
  const [submitWalletBankAccount, { isLoading: isSubmittingWallet }] = useSubmitWalletBankAccountMutation();

  useEffect(() => {
    if (!open) {
      setStep('bank');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    logAnalyticEvent(CleverTapEventsLegalReacceptance.webWalletBankReconnectModalView, {
      source: viewSource,
      seenCount: walletBankScreenSeenCount
    });
  }, [open, viewSource, walletBankScreenSeenCount]);

  const handleWalletSubmit = async (payload: WalletBankAccountPayload) => {
    try {
      await submitWalletBankAccount({ teamId, payload }).unwrap();
      onAfterComplete();
      setStep('success');
    } catch (error) {
      const submitErrorDetails = getWalletBankSubmitErrorDetails(error);
      dispatch(
        showNotifier({
          message: {
            primaryMessage: submitErrorDetails ?? FALLBACK_WALLET_BANK_SUBMIT_ERROR_MESSAGE
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
    }
  };

  const handleSuccessDone = () => {
    onAfterComplete();
    onClose();
  };

  const handleCloseClick = () => {
    if (step === 'success') {
      onAfterComplete();
    }
    onClose();
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCloseClick();
    }
  };

  return (
    <CustomModal open={open} enableOutsideClick={false} width="w-full md:w-[450px]" height="h-auto md:h-[660px]">
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 justify-end px-4 pt-4">
          <button
            type="button"
            className="cursor-pointer rounded-md p-1 text-primaryText 
focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
            onClick={handleCloseClick}
            onKeyDown={handleCloseKeyDown}
            aria-label="Close"
            tabIndex={0}>
            <NavCloseIcon id="wallet-bank-reconnect-modal-close" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {step === 'bank' ? (
            <WalletBankAccountScreen
              onSubmit={handleWalletSubmit}
              isSubmitting={isSubmittingWallet}
              viewSource={viewSource}
              walletBankScreenSeenCount={walletBankScreenSeenCount}
            />
          ) : (
            <LegalReacceptanceSuccessScreen onBackToApp={handleSuccessDone} />
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default WalletBankAccountReconnectModal;
